<script lang="ts">
  /// メールのタグ入力 (CU-86eyaj73w メール複数登録)。chip + remove の見た目は
  /// LawyerMultiSelect 系を踏襲し、select を自由文 input に差し替えた形。
  ///
  ///   - Enter で入力値を追加 (カンマ / 読点 / セミコロン / 改行区切りの
  ///     貼り付けは個別の値に分割し、値ごとに形式検証する)
  ///   - タグクリックで直接編集 (Enter / blur 確定・Escape 取消。blur=確定は
  ///     置換元 InlineField と同じ意味論。空・無変更の blur は取消扱い)
  ///   - 入力が空のときの Backspace / タグの × で削除
  ///
  /// FSD: shared は判断を持たない。追加/編集/削除の永続化はコールバックで親
  /// (widget / feature) に委ね、ここでは入力の分割・形式検証と結果の表示のみ行う。
  import { createEmail } from '@shared/value-objects';
  import { type EmailTagAddFailure, splitEmailInput } from './email-tag-input';

  interface EmailTagEntry {
    id: string;
    email: string;
  }

  interface Props {
    entries: EmailTagEntry[];
    disabled?: boolean;
    /** 追加。検証済みの値 (1..n) を渡す。追加できなかった値の理由を返す。 */
    onAdd: (values: string[]) => Promise<EmailTagAddFailure[]>;
    /** 1 件の値の書き換え。重複等は throw され、メッセージを表示する。 */
    onEdit: (id: string, value: string) => Promise<void>;
    /** 1 件の削除。 */
    onRemove: (id: string) => Promise<void>;
  }

  let { entries, disabled = false, onAdd, onEdit, onRemove }: Props = $props();

  let pending = $state('');
  let saving = $state(false);
  let failures = $state<EmailTagAddFailure[]>([]);
  let editingId = $state<string | null>(null);
  let editingValue = $state('');
  let editError = $state<string | null>(null);

  const INVALID_FORMAT_REASON = 'メールアドレスの形式が不正です';

  async function commitAdd() {
    if (saving) return;
    const values = splitEmailInput(pending);
    if (values.length === 0) {
      pending = '';
      return;
    }
    // 貼り付け分も値ごとに形式検証し、valid 値だけを親へ渡す。
    const invalid = values
      .filter((value) => createEmail(value) === null)
      .map((value) => ({ value, reason: INVALID_FORMAT_REASON }));
    const valid = values.filter((value) => createEmail(value) !== null);
    saving = true;
    try {
      const rejected = valid.length > 0 ? await onAdd(valid) : [];
      failures = [...invalid, ...rejected];
      pending = '';
    } catch (err) {
      failures = [
        { value: valid.join(', '), reason: err instanceof Error ? err.message : String(err) },
      ];
    } finally {
      saving = false;
    }
  }

  function onInputKeydown(ev: KeyboardEvent) {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      void commitAdd();
      return;
    }
    // 入力が空のときの Backspace は末尾タグの削除。
    const last = entries[entries.length - 1];
    if (ev.key === 'Backspace' && pending.length === 0 && last !== undefined && !saving) {
      ev.preventDefault();
      void remove(last.id);
    }
  }

  function onInputPaste(ev: ClipboardEvent) {
    const text = ev.clipboardData?.getData('text') ?? '';
    // 区切りを含む貼り付けは即分割して確定フローへ (単一値は通常入力扱い)。
    if (splitEmailInput(text).length > 1) {
      ev.preventDefault();
      pending = pending.length > 0 ? `${pending},${text}` : text;
      void commitAdd();
    }
  }

  function startEdit(entry: EmailTagEntry) {
    if (disabled || saving) return;
    editingId = entry.id;
    editingValue = entry.email;
    editError = null;
  }

  function cancelEdit() {
    editingId = null;
    editingValue = '';
    editError = null;
  }

  async function commitEdit() {
    if (saving || editingId === null) return;
    const value = editingValue.trim();
    if (value.length === 0) {
      editError = '値を入力してください (削除は × を使用)';
      return;
    }
    if (createEmail(value) === null) {
      editError = INVALID_FORMAT_REASON;
      return;
    }
    saving = true;
    try {
      await onEdit(editingId, value);
      cancelEdit();
    } catch (err) {
      editError = err instanceof Error ? err.message : String(err);
    } finally {
      saving = false;
    }
  }

  function onEditKeydown(ev: KeyboardEvent) {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      void commitEdit();
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      cancelEdit();
    }
  }

  /// blur は確定 (InlineField と同じ)。空・無変更なら取消扱いにして
  /// エラーを出さない。保存中の blur (disabled 化による focus 喪失) は
  /// commitEdit の saving ガードが no-op にする。
  function onEditBlur() {
    if (editingId === null) return;
    const value = editingValue.trim();
    const original = entries.find((entry) => entry.id === editingId)?.email;
    if (value.length === 0 || value === original) {
      if (!saving) cancelEdit();
      return;
    }
    void commitEdit();
  }

  async function remove(id: string) {
    if (saving) return;
    saving = true;
    try {
      await onRemove(id);
      failures = [];
      if (editingId === id) cancelEdit();
    } catch (err) {
      failures = [
        { value: '', reason: err instanceof Error ? err.message : String(err) },
      ];
    } finally {
      saving = false;
    }
  }
</script>

<div class="email-tags" data-testid="email-tag-input">
  {#if entries.length > 0}
    <ul class="chips">
      {#each entries as entry (entry.id)}
        <li class="chip">
          {#if editingId === entry.id}
            <!-- svelte-ignore a11y_autofocus -->
            <input
              class="chip-edit"
              type="email"
              bind:value={editingValue}
              onkeydown={onEditKeydown}
              onblur={onEditBlur}
              disabled={saving}
              autofocus
            />
          {:else}
            <button
              type="button"
              class="chip-label"
              title={disabled ? entry.email : 'クリックして編集'}
              disabled={disabled || saving}
              onclick={() => startEdit(entry)}>{entry.email}</button
            >
          {/if}
          {#if !disabled}
            <!-- mousedown の preventDefault で focus 奪取を防ぐ: 編集中の入力から
                 blur (= 確定) が先行すると saving 化で click が消え、削除操作が
                 編集保存に化けるため。編集中 entry の削除は remove 内で取消する。 -->
            <button
              type="button"
              class="chip-remove"
              aria-label="メールを削除"
              disabled={saving}
              onmousedown={(ev) => ev.preventDefault()}
              onclick={() => void remove(entry.id)}>×</button
            >
          {/if}
        </li>
      {/each}
    </ul>
  {:else if disabled}
    <span class="muted">-</span>
  {/if}

  {#if !disabled}
    <input
      class="add-input"
      type="text"
      placeholder="メールを入力して Enter で追加"
      bind:value={pending}
      onkeydown={onInputKeydown}
      onpaste={onInputPaste}
      disabled={saving}
    />
  {/if}

  {#if editError}
    <small class="error">{editError}</small>
  {/if}
  <!-- failures は同値の重複入力で (value, reason) が重なりうるため unkeyed
       (リストは round ごとに全置換なので keyed の恩恵もない)。 -->
  {#each failures as failure}
    <small class="error">
      {#if failure.value}<span class="failure-value">{failure.value}</span>: {/if}
      {failure.reason}
    </small>
  {/each}
</div>

<style>
  .email-tags {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #eef2ff;
    color: #3730a3;
    padding: 2px 4px 2px 8px;
    border-radius: 999px;
    font-size: 0.75rem;
  }
  .chip-label {
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font: inherit;
    padding: 0;
    word-break: break-all;
    text-align: left;
  }
  .chip-label:disabled {
    cursor: default;
  }
  .chip-edit {
    font: inherit;
    font-size: 0.75rem;
    border: 1px solid #a5b4fc;
    border-radius: 4px;
    padding: 0 4px;
    min-width: 180px;
  }
  .chip-remove {
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font-size: 0.85rem;
    line-height: 1;
    padding: 0 2px;
  }
  .add-input {
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: #fff;
    max-width: 320px;
  }
  .error {
    color: #b91c1c;
    font-size: 0.7rem;
  }
  .failure-value {
    font-weight: 700;
    word-break: break-all;
  }
  .muted {
    color: #6b7280;
    font-size: 0.875rem;
  }
</style>
